'use client';

import {
    Box,
    Typography,
    Grid,
    Button,
    Container,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';

const ACCENT = "#F15859";
const DARK_BG = "#132c4b";

function DocumentUploadIcon() {
    return (
        <Box sx={{ width: 52, height: 52, mb: 2.5 }}>
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 8C12 5.79086 13.7909 4 16 4H28L38 14V34C38 36.2091 36.2091 38 34 38H16C13.7909 38 12 36.2091 12 34V8Z"
                    stroke="#132c4b"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path d="M28 4V14H38" stroke="#132c4b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="18" y1="18" x2="25" y2="18" stroke="#132c4b" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="18" y1="23" x2="31" y2="23" stroke="#132c4b" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="18" y1="28" x2="24" y2="28" stroke="#132c4b" strokeWidth="2.2" strokeLinecap="round" />
                <circle cx="34" cy="34" r="10" fill="white" stroke="#F15859" strokeWidth="2.2" />
                <path d="M34 39V29M34 29L30.5 32.5M34 29L37.5 32.5" stroke="#F15859" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );
}

function SetupGearsIcon() {
    return (
        <Box sx={{ width: 52, height: 52, mb: 2.5 }}>
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M21 7V5M21 21V19M13 13H11M29 13H27M15.3 7.3L13.9 5.9M26.7 18.7L25.3 17.3M15.3 18.7L13.9 20.1M26.7 7.3L25.3 8.7"
                    stroke="#132c4b"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                />
                <circle cx="20" cy="13" r="5.5" stroke="#132c4b" strokeWidth="2.2" fill="white" />
                <circle cx="20" cy="13" r="2.2" stroke="#132c4b" strokeWidth="2" />
                <rect x="22" y="16" width="22" height="28" rx="4" fill="white" stroke="#132c4b" strokeWidth="2.2" />
                <path d="M26 23.5L28 25.5L31.5 22" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="34" y1="23.5" x2="39" y2="23.5" stroke="#132c4b" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M26 30L28 32L31.5 28.5" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="34" y1="30" x2="39" y2="30" stroke="#132c4b" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M26 36.5L28 38.5L31.5 35" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="34" y1="36.5" x2="39" y2="36.5" stroke="#132c4b" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        </Box>
    );
}

function AgentLaunchIcon() {
    return (
        <Box sx={{ width: 52, height: 52, mb: 2.5 }}>
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M26 33H14L9 38V14C9 11.7909 10.7909 10 13 10H31C33.2091 10 35 11.7909 35 14V20"
                    stroke="#132c4b"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="white"
                />
                <circle cx="16" cy="21" r="1.8" fill="#F15859" />
                <circle cx="22" cy="21" r="1.8" fill="#F15859" />
                <circle cx="28" cy="21" r="1.8" fill="#F15859" />
                <path
                    d="M38 14C38 14 32 17 30 22.5C29 25.5 30 28 30 28L35 33C35 33 37.5 34 40.5 33C46 31 49 25 49 25C49 25 43 26 40 23C37 20 38 14 38 14Z"
                    stroke="#F15859"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="white"
                />
                <circle cx="39.5" cy="22.5" r="1.5" fill="#F15859" />
                <path d="M29 27L25 29L27 32" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M35 33L37 37L34 35" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M29 33L26 36" stroke="#F15859" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        </Box>
    );
}

function ShieldSparkleIcon() {
    return (
        <Box sx={{ width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M22 8L33 12V21C33 27.5 28.5 33.5 22 35.5C15.5 33.5 11 27.5 11 21V12L22 8Z"
                    stroke="#F15859"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path d="M17 21L20.5 24.5L27 18" stroke="#F15859" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="4" y1="21" x2="7" y2="21" stroke="#F15859" strokeWidth="2" strokeLinecap="round" />
                <line x1="37" y1="21" x2="40" y2="21" stroke="#F15859" strokeWidth="2" strokeLinecap="round" />
                <line x1="22" y1="3" x2="22" y2="6" stroke="#F15859" strokeWidth="2" strokeLinecap="round" />
                <line x1="9" y1="9" x2="11.5" y2="11.5" stroke="#F15859" strokeWidth="2" strokeLinecap="round" />
                <line x1="35" y1="9" x2="32.5" y2="11.5" stroke="#F15859" strokeWidth="2" strokeLinecap="round" />
            </svg>
        </Box>
    );
}

export default function AiAgentContent() {
    const t = useTranslations('AiAgent');

    return (
        <Box component="main" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc', color: DARK_BG }}>

            {/* Hero Section */}
            <Box component="section" sx={{ bgcolor: DARK_BG, color: 'white', pt: { xs: 8, md: 12 }, pb: { xs: 4, md: 6 }, position: 'relative', overflow: 'hidden' }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
                    <Grid container spacing={6} alignItems="center">
                        {/* Left Hero Column */}
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ACCENT, mr: 1 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                                    {t('hero.subtitle')}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={2}>
                                {/* <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
                                    <Image src="/images/airx-ai-agent.png" alt="AirX AI Agent" width={48} height={48} style={{ objectFit: 'contain' }} />
                                </Box> */}
                                <Typography variant="h5" fontWeight="bold">AirX AI Agent</Typography>
                            </Box>
                            <Typography variant="h2" sx={{ fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' }, fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em', mb: 3 }}>
                                {t.rich('hero.title', { br: () => <br />, red: (chunks) => <Box component="span" sx={{ color: '#FFC8C8' }}>{chunks}</Box> })}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.6, mb: 3, maxWidth: 576 }}>
                                {t.rich('hero.desc', { br: () => <br /> })}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                                <Button
                                    component={Link}
                                    href="/contact?tab=demo"
                                    target="_self"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        backgroundColor: ACCENT,
                                        fontWeight: 'bold',
                                        px: { xs: 4, sm: 6 },
                                        py: 1.5,
                                        borderRadius: '10px',
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        boxShadow: '0 4px 14px 0 rgba(225, 29, 72, 0.39)',
                                        '&:hover': {
                                            backgroundColor: '#BE123C',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(225, 29, 72, 0.23)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    {t('hero.demo_btn')}
                                </Button>
                                <Button
                                    href="#price"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        color: 'white',
                                        borderColor: 'rgba(255,255,255,0.3)',
                                        fontWeight: 'bold',
                                        px: { xs: 4, sm: 6 },
                                        py: 1.5,
                                        borderRadius: '10px',
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            borderColor: 'rgba(255,255,255,0.3)',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    {t('hero.plans_btn')}
                                </Button>
                            </Box>
                            <Box display="flex" alignItems="baseline" gap={1} mt={1}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{t('hero.monthly')}</Typography>
                                <Typography variant="h3" fontWeight="800">{t('hero.price')}</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{t('hero.unit')}</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', ml: 1 }}>{t('hero.tax')}</Typography>
                            </Box>
                        </Grid>

                        {/* Right Hero Visual Card */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    maxWidth: { xs: 520, md: "100%" },
                                    mx: "auto",
                                    aspectRatio: "1 / 1",
                                    borderRadius: "1rem",
                                    overflow: "hidden",
                                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.4)",
                                    backgroundColor: "#000",
                                }}
                            >
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    preload="auto"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                >
                                    <source src="/airx-aI-agent-hero-60s.mp4" type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Quick Summary Bar */}
            <Box sx={{ bgcolor: DARK_BG, borderTop: '1px solid rgba(255,255,255,0.1)', py: 3 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3} sx={{ '& > .MuiGrid-root': { borderRight: { md: '1px solid rgba(255,255,255,0.1)' } }, '& > .MuiGrid-root:last-child': { borderRight: 'none' } }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={1}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.875rem' }}><CheckIcon fontSize="small" /></Box>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold" color="white">{t('summary.item1.title')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t('summary.item1.desc')}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={1}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.875rem' }}><AccessTimeIcon fontSize="small" /></Box>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold" color="white">{t('summary.item2.title')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t('summary.item2.desc')}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={1}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.875rem' }}><PersonOutlineIcon fontSize="small" /></Box>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold" color="white">{t('summary.item3.title')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t('summary.item3.desc')}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Why AirX AI Agent? Section */}
            <Box component="section" id="why" sx={{ py: 10, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" maxWidth={768} mx="auto" mb={8}>
                        <Chip label={t('why.chip')} sx={{ bgcolor: ACCENT, color: 'white', fontWeight: 700, mb: 3 }} />
                        <Typography variant="h4" fontWeight="800" color={DARK_BG} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            {t.rich('why.title', { br: () => <br /> })}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: { xs: '0.875rem', md: '1rem' }, lineHeight: 1.6 }}>
                            {t('why.desc')}
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {[
                            { step: t('why.steps.0.step'), title: t('why.steps.0.title'), desc: t('why.steps.0.desc') },
                            { step: t('why.steps.1.step'), title: t('why.steps.1.title'), desc: t('why.steps.1.desc') },
                            { step: t('why.steps.2.step'), title: t('why.steps.2.title'), desc: t('why.steps.2.desc') }
                        ].map((item, idx) => (
                            <Grid size={{ xs: 12, md: 4 }} key={idx}>
                                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', textAlign: 'center', height: '100%' }}>
                                    <Box sx={{ width: 48, height: 48, bgcolor: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                                        <Typography variant="h6" fontWeight="bold" color={ACCENT}>{item.step}</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold" color={DARK_BG} mb={2}>{item.title}</Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6, textAlign: 'left' }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* AI + HUMAN Section */}
            <Box component="section" id="flow" sx={{ py: 10, bgcolor: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" maxWidth={768} mx="auto" mb={8}>
                        <Chip label={t('flow.chip')} sx={{ bgcolor: ACCENT, color: 'white', fontWeight: 700, mb: 3 }} />
                        <Typography variant="h4" fontWeight="800" color={DARK_BG} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            {t('flow.title')}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: { xs: '0.875rem', md: '1rem' }, lineHeight: 1.6 }}>
                            {t('flow.desc')}
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 6, border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
                                <Chip label={t('flow.card1.badge')} size="small" sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 'bold', fontSize: '0.625rem' }} />
                                <Typography variant="h6" fontWeight="bold" color={DARK_BG} mb={3} pb={2} borderBottom="1px solid #f1f5f9">{t('flow.card1.title')}</Typography>

                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', fontWeight: 'bold', color: DARK_BG, fontSize: '0.875rem', borderRadius: 3 }}>
                                        {t('flow.card1.trigger')}
                                    </Paper>
                                    <Box textAlign="center" color="#cbd5e1">↓</Box>
                                    <Paper elevation={3} sx={{ p: 2.5, textAlign: 'center', bgcolor: DARK_BG, color: 'white', borderRadius: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>{t('flow.card1.agent')}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, display: 'block' }}>
                                            {t.rich('flow.card1.agent_desc', { br: () => <br /> })}
                                        </Typography>
                                    </Paper>
                                    <Box textAlign="center" color="#cbd5e1">↓</Box>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderRadius: 3 }}>
                                                <Typography variant="caption" color="#64748b" mb={0.5}>{t('flow.card1.branch1_title')}</Typography>
                                                <Typography variant="body2" fontWeight="bold" color={DARK_BG}>{t('flow.card1.branch1_desc')}</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderRadius: 3 }}>
                                                <Typography variant="caption" color="#64748b" mb={0.5}>{t('flow.card1.branch2_title')}</Typography>
                                                <Typography variant="body2" fontWeight="bold" color={DARK_BG} mb={0.5}>{t.rich('flow.card1.branch2_name', { br: () => <br /> })}</Typography>
                                                <Typography sx={{ fontSize: '0.625rem', color: '#64748b', mt: 1 }}>{t('flow.card1.branch2_desc')}</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 6, border: '1px solid #d1fae5', bgcolor: 'rgba(236, 253, 245, 0.5)', position: 'relative', overflow: 'hidden' }}>
                                <Chip label={t('flow.card2.badge')} size="small" variant="outlined" sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'white', borderColor: '#e2e8f0', color: '#64748b', fontWeight: 'bold', fontSize: '0.625rem' }} />
                                <Typography variant="h6" fontWeight="bold" color={DARK_BG} mb={3} pb={2} borderBottom="1px solid #d1fae5">{t('flow.card2.title')}</Typography>

                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', fontWeight: 'bold', color: DARK_BG, fontSize: '0.875rem', borderRadius: 3, borderColor: '#a7f3d0' }}>
                                        {t('flow.card2.trigger')}
                                    </Paper>
                                    <Box textAlign="center" color="#6ee7b7">↓</Box>
                                    <Paper elevation={3} sx={{ p: 2.5, textAlign: 'center', bgcolor: '#047857', color: 'white', borderRadius: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>{t('flow.card2.agent')}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, display: 'block' }}>
                                            {t.rich('flow.card2.agent_desc', { br: () => <br /> })}
                                        </Typography>
                                    </Paper>
                                    <Box textAlign="center" color="#6ee7b7">↓</Box>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 3, borderColor: '#a7f3d0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography variant="caption" color="#64748b" mb={2}>{t('flow.card2.branch_title')}</Typography>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold" color={DARK_BG}>{t('flow.card2.branch1_name')}</Typography>
                                                <Typography sx={{ fontSize: '0.625rem', color: '#64748b' }}>{t('flow.card2.branch1_desc')}</Typography>
                                            </Box>
                                            <Box sx={{ borderTop: '1px solid #f1f5f9', my: 1 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold" color={DARK_BG}>{t('flow.card2.branch2_name')}</Typography>
                                                <Typography sx={{ fontSize: '0.625rem', color: '#64748b' }}>{t('flow.card2.branch2_desc')}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Pricing Section */}
            <Box component="section" id="price" sx={{ py: 10, bgcolor: 'white', borderTop: '1px solid rgba(226,232,240,0.6)' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" maxWidth={768} mx="auto" mb={8}>
                        <Chip label={t('plans.chip')} sx={{ bgcolor: ACCENT, color: 'white', fontWeight: 700, mb: 3 }} />
                        <Typography variant="h4" fontWeight="800" color={DARK_BG} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            {t('plans.title')}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: { xs: '0.875rem', md: '1rem' }, lineHeight: 1.6 }}>
                            {t('plans.desc')}
                        </Typography>
                    </Box>

                    <Grid container spacing={3} alignItems="stretch">
                        {/* Plan 1 */}
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: 4 } }}>
                                <Typography variant="h6" fontWeight="800" color={DARK_BG} mb={0.5}>{t('plans.items.0.name')}</Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#64748b', mb: 3, minHeight: 32 }}>{t('plans.items.0.desc')}</Typography>
                                <Box mb={3}>
                                    <Typography variant="h4" fontWeight="800" color={DARK_BG} component="span">{t('plans.items.0.price')}</Typography>
                                    <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8', mt: 0.5 }}>{t('plans.items.0.tax')}</Typography>
                                </Box>
                                <Stack spacing={1.5} sx={{ mb: 4 }}>
                                    {[0, 1, 2].map((idx) => (
                                        <Box display="flex" alignItems="flex-start" gap={1} key={idx}>
                                            <Typography sx={{ color: ACCENT, fontWeight: 'bold', fontSize: '0.875rem' }}>✓</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {t.rich(`plans.items.0.features.${idx}`, { bold: (chunks) => <Box component="span" sx={{ fontWeight: 'bold', color: DARK_BG }}>{chunks}</Box> })}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Box mt="auto">
                                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, textAlign: 'center', fontSize: '0.625rem', color: '#64748b', fontWeight: 'bold', border: '1px solid #f1f5f9' }}>
                                        {t('plans.items.0.bottom')}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Plan 2 */}
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <Paper elevation={4} sx={{ p: 3, borderRadius: 4, border: '2px solid', borderColor: ACCENT, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', transform: { lg: 'translateY(-8px)' } }}>
                                <Chip label={t('plans.items.1.badge')} sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', bgcolor: ACCENT, color: 'white', fontWeight: 'bold', fontSize: '0.625rem', height: 24, boxShadow: 2 }} />
                                <Typography variant="h6" fontWeight="800" color={DARK_BG} mb={0.5}>{t('plans.items.1.name')}</Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#64748b', mb: 3, minHeight: 32 }}>{t('plans.items.1.desc')}</Typography>
                                <Box mb={3}>
                                    <Typography variant="h4" fontWeight="800" color={DARK_BG} component="span">{t('plans.items.1.price')}</Typography>
                                    <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8', mt: 0.5 }}>{t('plans.items.1.tax')}</Typography>
                                </Box>
                                <Stack spacing={1.5} sx={{ mb: 4 }}>
                                    {[0, 1, 2].map((idx) => (
                                        <Box display="flex" alignItems="flex-start" gap={1} key={idx}>
                                            <Typography sx={{ color: ACCENT, fontWeight: 'bold', fontSize: '0.875rem' }}>✓</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {t.rich(`plans.items.1.features.${idx}`, { bold: (chunks) => <Box component="span" sx={{ fontWeight: 'bold', color: DARK_BG }}>{chunks}</Box> })}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Box mt="auto">
                                    <Box sx={{ bgcolor: '#fef2f2', p: 1.5, borderRadius: 1, textAlign: 'center', fontSize: '0.625rem', color: ACCENT, fontWeight: 'bold', border: '1px solid #fee2e2' }}>
                                        {t('plans.items.1.bottom')}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Plan 3 */}
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: 4 } }}>
                                <Typography variant="h6" fontWeight="800" color={DARK_BG} mb={0.5}>{t('plans.items.2.name')}</Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#64748b', mb: 3, minHeight: 32 }}>{t('plans.items.2.desc')}</Typography>
                                <Box mb={3}>
                                    <Typography variant="h4" fontWeight="800" color={DARK_BG} component="span">{t('plans.items.2.price')}</Typography>
                                    <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8', mt: 0.5 }}>{t('plans.items.2.tax')}</Typography>
                                </Box>
                                <Stack spacing={1.5} sx={{ mb: 4 }}>
                                    {[0, 1, 2, 3].map((idx) => (
                                        <Box display="flex" alignItems="flex-start" gap={1} key={idx}>
                                            <Typography sx={{ color: ACCENT, fontWeight: 'bold', fontSize: '0.875rem' }}>✓</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {t.rich(`plans.items.2.features.${idx}`, { bold: (chunks) => <Box component="span" sx={{ fontWeight: 'bold', color: DARK_BG }}>{chunks}</Box> })}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Box mt="auto">
                                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, textAlign: 'center', fontSize: '0.625rem', color: '#64748b', fontWeight: 'bold', border: '1px solid #f1f5f9' }}>
                                        {t('plans.items.2.bottom')}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Plan 4 */}
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: 4 } }}>
                                <Typography variant="h6" fontWeight="800" color={DARK_BG} mb={0.5}>{t('plans.items.3.name')}</Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#64748b', mb: 3, minHeight: 32 }}>{t('plans.items.3.desc')}</Typography>
                                <Box mb={3}>
                                    <Typography variant="h4" fontWeight="800" color={DARK_BG} component="span">{t('plans.items.3.price')}</Typography>
                                    <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8', mt: 0.5 }}>{t('plans.items.3.tax')}</Typography>
                                </Box>
                                <Stack spacing={1.5} sx={{ mb: 4 }}>
                                    {[0, 1, 2, 3].map((idx) => (
                                        <Box display="flex" alignItems="flex-start" gap={1} key={idx}>
                                            <Typography sx={{ color: ACCENT, fontWeight: 'bold', fontSize: '0.875rem' }}>✓</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {t.rich(`plans.items.3.features.${idx}`, { bold: (chunks) => <Box component="span" sx={{ fontWeight: 'bold', color: DARK_BG }}>{chunks}</Box> })}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Box mt="auto">
                                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, textAlign: 'center', fontSize: '0.625rem', color: '#64748b', fontWeight: 'bold', border: '1px solid #f1f5f9' }}>
                                        {t('plans.items.3.bottom')}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            mt: 5,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            p: { xs: 3, sm: 4, md: 5 },
                            borderRadius: 4
                        }}
                    >
                        {/* Title & Subtitle */}
                        <Box mb={3}>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontWeight: 800,
                                    color: DARK_BG,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                                    mb: 1
                                }}
                            >
                                {t('plans.initial_cost.title')}
                            </Typography>
                            <Typography sx={{ color: '#475569', fontSize: { xs: '0.875rem', sm: '0.9375rem' }, lineHeight: 1.6 }}>
                                {t('plans.initial_cost.desc')}
                            </Typography>
                        </Box>

                        {/* Formula Banner */}
                        <Box
                            sx={{
                                bgcolor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: 3,
                                p: { xs: 2.5, sm: 3 },
                                mb: 4
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.625rem' },
                                    fontWeight: 800,
                                    color: DARK_BG,
                                    letterSpacing: '-0.01em',
                                    mb: 0.75
                                }}
                            >
                                {t('plans.initial_cost.formula')}
                            </Typography>
                            <Typography sx={{ color: '#475569', fontSize: { xs: '0.8125rem', sm: '0.875rem' }, lineHeight: 1.5 }}>
                                {t('plans.initial_cost.formula_note')}
                            </Typography>
                        </Box>

                        {/* Example Section & Table */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 800,
                                    color: DARK_BG,
                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                    mb: 2
                                }}
                            >
                                {t('plans.initial_cost.example_title')}
                            </Typography>

                            <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0',
                                    bgcolor: 'white'
                                }}
                            >
                                <Table sx={{ minWidth: 540 }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: DARK_BG }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1.75, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.th_item')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1.75, px: 2, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.th_qty')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1.75, px: 2, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.th_unit_price')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1.75, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.th_amount')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow sx={{ bgcolor: 'white' }}>
                                            <TableCell sx={{ color: '#1e293b', fontWeight: 700, fontSize: '0.9375rem', py: 2, px: 3, borderBottom: '1px solid #e2e8f0' }}>
                                                {t('plans.initial_cost.table.row1_item')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: '#334155', fontSize: '0.9375rem', py: 2, px: 2, borderBottom: '1px solid #e2e8f0' }}>
                                                {t('plans.initial_cost.table.row1_qty')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: '#334155', fontSize: '0.9375rem', py: 2, px: 2, borderBottom: '1px solid #e2e8f0' }}>
                                                {t('plans.initial_cost.table.row1_unit_price')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: DARK_BG, fontWeight: 800, fontSize: '1.25rem', py: 2, px: 3, borderBottom: '1px solid #e2e8f0' }}>
                                                {t('plans.initial_cost.table.row1_amount')}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: '#eef5f8' }}>
                                            <TableCell sx={{ color: '#1e293b', fontWeight: 700, fontSize: '0.9375rem', py: 2, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.row2_item')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: '#334155', fontSize: '0.9375rem', py: 2, px: 2, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.row2_qty')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: '#334155', fontSize: '0.9375rem', py: 2, px: 2, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.row2_unit_price')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: DARK_BG, fontWeight: 800, fontSize: '1.25rem', py: 2, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.row2_amount')}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: '#fff1eb' }}>
                                            <TableCell sx={{ color: '#1e293b', fontWeight: 800, fontSize: '1rem', py: 2.25, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.total_item')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ borderBottom: 'none' }} />
                                            <TableCell align="center" sx={{ borderBottom: 'none' }} />
                                            <TableCell align="right" sx={{ color: '#c2410c', fontWeight: 800, fontSize: '1.375rem', py: 2.25, px: 3, borderBottom: 'none' }}>
                                                {t('plans.initial_cost.table.total_amount')}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>

                    <Box mt={4} display="flex" justifyContent="flex-end">
                        <Button
                            component={Link}
                            href="/contact"
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: ACCENT,
                                color: 'white',
                                fontWeight: 'bold',
                                px: { xs: 4, sm: 6 },
                                py: 1.5,
                                borderRadius: '10px',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                boxShadow: '0 4px 14px 0 rgba(225, 29, 72, 0.39)',
                                '&:hover': {
                                    backgroundColor: '#BE123C',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(225, 29, 72, 0.23)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            {t('plans.footer.btn')}
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* ONBOARDING Section */}
            <Box component="section" id="onboarding" sx={{ py: { xs: 8, md: 10 }, bgcolor: '#f8fafc' }}>
                <Container maxWidth="lg">
                    {/* Header */}
                    <Box textAlign="center" maxWidth={860} mx="auto" mb={{ xs: 5, md: 6 }}>
                        <Chip
                            label={t('onboarding.chip')}
                            sx={{
                                bgcolor: ACCENT,
                                color: 'white',
                                fontWeight: 700,
                                mb: 2.5,
                                px: 1,
                                height: 30,
                                fontSize: '0.875rem'
                            }}
                        />
                        <Typography
                            variant="h4"
                            fontWeight="800"
                            color={DARK_BG}
                            mb={2}
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' },
                                lineHeight: 1.3,
                                letterSpacing: '-0.01em'
                            }}
                        >
                            {t('onboarding.title')}
                        </Typography>
                        <Typography sx={{ color: '#475569', fontSize: { xs: '0.875rem', sm: '0.9375rem' }, lineHeight: 1.8 }}>
                            {t.rich('onboarding.desc', { br: () => <br /> })}
                        </Typography>
                    </Box>

                    {/* 3 Step Cards */}
                    <Grid container spacing={3}>
                        {[
                            {
                                step: t('onboarding.steps.0.step'),
                                title: t('onboarding.steps.0.title'),
                                desc: t('onboarding.steps.0.desc'),
                                icon: <DocumentUploadIcon />
                            },
                            {
                                step: t('onboarding.steps.1.step'),
                                title: t('onboarding.steps.1.title'),
                                desc: t('onboarding.steps.1.desc'),
                                icon: <SetupGearsIcon />
                            },
                            {
                                step: t('onboarding.steps.2.step'),
                                title: t('onboarding.steps.2.title'),
                                desc: t('onboarding.steps.2.desc'),
                                icon: <AgentLaunchIcon />
                            }
                        ].map((item, idx) => (
                            <Grid size={{ xs: 12, md: 4 }} key={idx}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 3, sm: 3.5, md: 4 },
                                        borderRadius: 4,
                                        border: '1px solid #e2e8f0',
                                        bgcolor: 'white',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.03)',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 10px 25px -4px rgba(0, 0, 0, 0.06)'
                                        }
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: ACCENT,
                                            fontWeight: 800,
                                            fontSize: '1.125rem',
                                            mb: 2
                                        }}
                                    >
                                        {item.step}
                                    </Typography>
                                    {item.icon}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            color: DARK_BG,
                                            fontSize: '1.125rem',
                                            mb: 1.5
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.65
                                        }}
                                    >
                                        {item.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Support Callout Banner */}
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 3,
                            bgcolor: '#fff5f5',
                            border: '1px solid #ffdcd9',
                            borderRadius: 4,
                            p: { xs: 2.5, sm: 3 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 2, sm: 3 }
                        }}
                    >
                        <ShieldSparkleIcon />
                        <Box sx={{ width: '1px', height: 44, bgcolor: '#ffdcd9', display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 800,
                                    color: ACCENT,
                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                    mb: 0.5
                                }}
                            >
                                {t('onboarding.banner_title')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#475569',
                                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                    lineHeight: 1.6
                                }}
                            >
                                {t('onboarding.banner_desc')}
                            </Typography>
                        </Box>
                    </Paper>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box component="section" id="contact" sx={{ py: 10, bgcolor: DARK_BG, color: 'white', textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Chip label={t('cta.chip')} sx={{ bgcolor: ACCENT, color: 'white', fontWeight: 700, mb: 3 }} />
                    <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '2.25rem' }, fontWeight: 800, lineHeight: 1.4, mb: 4 }}>
                        {t.rich('cta.title', { br: () => <br /> })}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.875rem', md: '1rem' }, lineHeight: 1.6, mb: 5, maxWidth: 672, mx: 'auto' }}>
                        {t.rich('cta.desc', { br: () => <br /> })}
                    </Typography>
                    <Button
                        component={Link}
                        href="/contact"
                        variant="contained"
                        size="large"
                        sx={{
                            backgroundColor: ACCENT,
                            color: 'white',
                            fontWeight: 'bold',
                            px: { xs: 5, sm: 8 },
                            py: 1.5,
                            borderRadius: '10px',
                            fontSize: '1rem',
                            textTransform: 'none',
                            boxShadow: '0 4px 14px 0 rgba(225, 29, 72, 0.39)',
                            mb: 3,
                            '&:hover': {
                                backgroundColor: '#BE123C',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(225, 29, 72, 0.23)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {t('cta.btn')}
                    </Button>
                    {/* <Box mt={2}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {t.rich('cta.company', { br: () => <br /> })}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography component="span" fontWeight="bold" color="white" fontSize="1rem">{t('cta.phone')}</Typography>
                            <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', ml: 1 }}>{t('cta.time')}</Typography>
                        </Box>
                    </Box> */}
                </Container>
            </Box>
        </Box>
    );
}
